<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { analyticsTrack, EventNames } from '@/plugins/usage-analytics'
import { createLiquidGlass, type LiquidGlassHandle } from './liquidGlass'

const emit = defineEmits(['login'])

function handleLoginClick() {
  analyticsTrack(EventNames.ONBOARD_LOGIN_CLICK, {})
  emit('login')
}

// --- WebGL liquid glass (github.com/archisvaze/liquid-glass, webgl version) ---
const cardEl = ref<HTMLElement | null>(null)
const glassCanvas = ref<HTMLCanvasElement | null>(null)
const glassActive = ref(false)

// Paint the same textured backdrop as the DOM `.hero-backdrop` so the area the
// glass refracts is seamless with the surrounding page.
// Grid scrolls up at GRID_SPEED css px/s — kept in sync with the CSS
// `.hero-backdrop` keyframe (one 20px cell every 3.333s) so the area inside the
// glass and the surrounding page move together.
const GRID_SPEED = 6
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function drawBackdrop(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number, elapsedMs: number) {
  ctx.clearRect(0, 0, w, h)
  ctx.fillStyle = '#f9fbf9'
  ctx.fillRect(0, 0, w, h)

  const light = ctx.createRadialGradient(w * 0.16, h * 0.08, 0, w * 0.16, h * 0.08, Math.max(w, h) * 0.55)
  light.addColorStop(0, 'rgba(255,255,255,0.9)')
  light.addColorStop(0.6, 'rgba(255,255,255,0)')
  ctx.fillStyle = light
  ctx.fillRect(0, 0, w, h)

  const dark = ctx.createRadialGradient(w * 0.9, h * 0.98, 0, w * 0.9, h * 0.98, Math.max(w, h) * 0.7)
  dark.addColorStop(0, 'rgba(15,23,42,0.1)')
  dark.addColorStop(0.7, 'rgba(15,23,42,0)')
  ctx.fillStyle = dark
  ctx.fillRect(0, 0, w, h)

  // Excel-style cell grid; horizontal lines scroll up over time
  const cw = 54 * dpr
  const ch = 20 * dpr
  const offset = prefersReducedMotion
    ? 0
    : ((((elapsedMs / 1000) * GRID_SPEED * dpr) % ch) + ch) % ch
  ctx.strokeStyle = 'rgba(15,23,42,0.06)'
  ctx.lineWidth = Math.max(1, dpr)
  ctx.beginPath()
  for (let x = 0; x <= w; x += cw) {
    const px = Math.round(x) + 0.5
    ctx.moveTo(px, 0)
    ctx.lineTo(px, h)
  }
  for (let y = -offset; y <= h; y += ch) {
    const py = Math.round(y) + 0.5
    ctx.moveTo(0, py)
    ctx.lineTo(w, py)
  }
  ctx.stroke()
}

let glassHandle: LiquidGlassHandle | null = null

onMounted(() => {
  const canvas = glassCanvas.value
  const card = cardEl.value
  if (!canvas || !card) return

  const getRect = () => {
    const c = canvas.getBoundingClientRect()
    const r = card.getBoundingClientRect()
    return { x: r.left - c.left, y: r.top - c.top, w: r.width, h: r.height }
  }

  glassHandle = createLiquidGlass(canvas, getRect, drawBackdrop, {
    radius: 28,
    bezel: 16,
    thickness: 60,
    ior: 1.5,
    blur: 0,
    specular: 0.3,
    tint: 0,
    shadow: 0,
  })
  glassActive.value = !!glassHandle
})

onBeforeUnmount(() => {
  glassHandle?.destroy()
  glassHandle = null
})
</script>
<template>
  <div
    class="onboard-root relative flex min-h-full flex-col justify-start px-4 py-6 sm:px-6"
    :class="{ 'glass-on': glassActive }"
  >
    <!-- full-page textured backdrop (also what the WebGL glass refracts) -->
    <div aria-hidden="true" class="hero-backdrop"></div>
    <!-- WebGL liquid-glass layer; transparent outside the card -->
    <canvas ref="glassCanvas" aria-hidden="true" class="glass-canvas"></canvas>

    <!-- Liquid-glass edge-refraction filter (progressive enhancement) -->
    <svg aria-hidden="true" class="glass-filter" width="0" height="0">
      <defs>
        <filter id="liquid-glass" x="-20%" y="-20%" width="140%" height="140%">
          <feTurbulence type="fractalNoise" baseFrequency="0.011 0.015" numOctaves="2" seed="7" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="2" result="noiseBlur" />
          <feDisplacementMap in="SourceGraphic" in2="noiseBlur" scale="42" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
    </svg>

    <div class="mx-auto w-full max-w-md">
      <!-- Single liquid-glass card holding everything -->
      <div
        ref="cardEl"
        class="hero-card relative rounded-3xl px-6 pb-7 pt-7"
      >
        <span aria-hidden="true" class="glass-edge"></span>
        <span aria-hidden="true" class="card-shine"></span>

        <h2 class="hero-title text-xl font-semibold tracking-tight text-gray-900 text-balance">
          Spark for Excel
        </h2>
        <p class="hero-sub mt-1.5 max-w-xs text-sm leading-relaxed text-gray-600 text-pretty">
          Pull commodity pricing data directly into your spreadsheets.
        </p>

        <!-- Feature pills -->
        <div class="pill-row mt-5 flex flex-wrap gap-2">
          <span class="feature-pill bg-green-500 text-white">
            <SparkIcon icon="freight-alt-line" size="14" />
            Freight
          </span>
          <span class="feature-pill bg-basis text-white">
            <SparkIcon icon="basis" size="14" />
            Cargo
          </span>
          <span class="feature-pill bg-access text-black">
            <SparkIcon icon="accessLogo" size="14" />
            Access
          </span>
          <span class="feature-pill bg-gas text-white">
            <SparkIcon icon="gas" size="14" />
            European Gas
          </span>
          <span class="feature-pill bg-gas-800 text-white">
            <SparkIcon icon="gasTransit" size="14" />
            Gas Transit
          </span>
        </div>

        <!-- Info -->
        <div class="mt-7 space-y-5 text-base text-gray-600">
          <div>
            <div class="text-base font-semibold text-gray-900">What is Spark?</div>
            <p class="mt-1 leading-relaxed">
              Spark is redefining how the commodity markets understand price.
              Backed by Kpler, ICE and EEX, we offer transparency and
              insight into LNG and European Gas markets.
            </p>
          </div>

          <div>
            <div class="text-base font-semibold text-gray-900">
              What data can I get?
            </div>
            <p class="mt-1 leading-relaxed">
              Your access depends on your Spark account type. Full data is
              available to Premium users (<a
                href="https://www.sparkcommodities.com/"
                target="_blank"
                rel="noopener noreferrer"
                class="font-medium text-green hover:underline"
                >learn more</a
              >).
            </p>
          </div>
        </div>

        <!-- Action -->
        <section class="mt-7">
          <button type="button" class="get-started" @click="handleLoginClick">
            Get started
          </button>
        </section>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Full-page neutral textured backdrop the glass card refracts */
.onboard-root {
  isolation: isolate;
}
.hero-backdrop {
  position: absolute;
  inset: 0;
  z-index: -2;
  pointer-events: none;
  /* match the login page shade (bg-shade-light) so the views align */
  background-color: #f9fbf9;
  background-image:
    /* Excel-style cell grid (lines on top) */
    linear-gradient(to right, rgba(15, 23, 42, 0.06) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(15, 23, 42, 0.06) 1px, transparent 1px),
    /* broad light + dark tonal areas so the glass has gradients to bend */
    radial-gradient(55% 45% at 16% 8%, rgba(255, 255, 255, 0.9), transparent 60%),
    radial-gradient(70% 55% at 90% 98%, rgba(15, 23, 42, 0.1), transparent 70%);
  background-size: 54px 20px, 54px 20px, 100% 100%, 100% 100%;
  /* scroll the grid up one cell, looping; tonal layers stay put */
  animation: grid-scroll 3.333s linear infinite;
}

@keyframes grid-scroll {
  from {
    background-position: 0 0, 0 0, 0 0, 0 0;
  }
  to {
    background-position: 0 -20px, 0 -20px, 0 0, 0 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-backdrop {
    animation: none;
  }
}

/* WebGL liquid-glass layer: full-cover, transparent outside the card region */
.glass-canvas {
  position: absolute;
  inset: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* When the WebGL glass is running, the shader provides the surface — let it show
   through the card by clearing the CSS glass fill / blur / edge layers. */
.glass-on .hero-card {
  background: transparent;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
  /* drop the white inset glows/rim — the shader is the surface now;
     keep only a soft outer drop shadow for lift */
  box-shadow: 0 20px 44px -22px rgba(16, 24, 40, 0.4);
}
.glass-on .glass-edge,
.glass-on .card-shine,
.glass-on .hero-card::after {
  display: none;
}

/* Thick "liquid glass" slab */
.hero-card {
  isolation: isolate;
  border-radius: 1.75rem;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px) saturate(180%) brightness(1.04);
  -webkit-backdrop-filter: blur(8px) saturate(180%) brightness(1.04);
  box-shadow:
    /* crisp top + bottom edge highlights = glass thickness */
    inset 0 1.5px 0 rgba(255, 255, 255, 0.95),
    inset 0 -1.5px 1px rgba(255, 255, 255, 0.55),
    /* bright rim */
    inset 0 0 0 1px rgba(255, 255, 255, 0.5),
    /* inner top glow + bottom shade give the slab depth */
    inset 0 16px 32px -16px rgba(255, 255, 255, 0.8),
    inset 0 -20px 36px -22px rgba(16, 24, 40, 0.22),
    /* floating-slab drop shadow */
    0 1px 1px rgba(16, 24, 40, 0.05),
    0 6px 14px rgba(16, 24, 40, 0.08),
    0 34px 64px -24px rgba(16, 24, 40, 0.42);
}

/* Fallback where backdrop-filter is unsupported: keep it legible */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .hero-card {
    background: rgba(255, 255, 255, 0.8);
  }
}

/* hidden filter defs holder */
.glass-filter {
  position: absolute;
  width: 0;
  height: 0;
}

/* edge lensing: refract the backdrop near the rim for a thick-glass look */
.glass-edge {
  position: absolute;
  inset: 0;
  z-index: -4;
  border-radius: inherit;
  pointer-events: none;
  -webkit-backdrop-filter: blur(2px) url(#liquid-glass);
  backdrop-filter: blur(2px) url(#liquid-glass);
  /* concentrate the distortion at the rim, leave the centre clear */
  -webkit-mask: radial-gradient(135% 135% at 50% 50%, transparent 56%, #000 82%);
  mask: radial-gradient(135% 135% at 50% 50%, transparent 56%, #000 82%);
}

/* static specular: bright top-left corner highlight + soft diagonal glaze */
.hero-card::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -3;
  border-radius: inherit;
  pointer-events: none;
  background:
    radial-gradient(120% 70% at 14% -10%, rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0) 44%),
    linear-gradient(122deg, rgba(255, 255, 255, 0) 42%, rgba(255, 255, 255, 0.45) 50%, rgba(255, 255, 255, 0) 58%);
}

/* soft reflection of a light source across the top of the glass */
.card-shine {
  position: absolute;
  inset: 0;
  z-index: -2;
  border-radius: inherit;
  pointer-events: none;
  overflow: hidden;
}
.card-shine::before {
  content: '';
  position: absolute;
  top: -90px;
  left: -8%;
  width: 72%;
  height: 320px;
  transform: rotate(20deg);
  background: radial-gradient(closest-side, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0) 72%);
  filter: blur(10px);
}

/* Polished / etched-into-glass title: a deep slate gradient with a light bevel */
.hero-title {
  background-image: linear-gradient(176deg, #46505f 0%, #2d3748 42%, #1a202c 56%, #0c1014 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 0.4px rgba(255, 255, 255, 0.75));
}

/* Subtly embossed / letterpressed subtitle */
.hero-sub {
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.85);
}

.feature-pill {
  @apply inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold;
  /* glossy candy-button finish */
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.28), rgba(255, 255, 255, 0) 55%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.35),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px rgba(16, 24, 40, 0.2);
  animation: pill-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.pill-row .feature-pill:nth-child(1) { animation-delay: 0.04s; }
.pill-row .feature-pill:nth-child(2) { animation-delay: 0.1s; }
.pill-row .feature-pill:nth-child(3) { animation-delay: 0.16s; }
.pill-row .feature-pill:nth-child(4) { animation-delay: 0.22s; }
.pill-row .feature-pill:nth-child(5) { animation-delay: 0.28s; }

@keyframes pill-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* "Get started" — the product-tag look as a primary CTA */
.get-started {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border-radius: 9999px;
  padding: 0.7rem 1.4rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background-color: #21c08e;
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0) 55%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.45),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12),
    0 2px 6px rgba(16, 24, 40, 0.18),
    0 10px 22px -10px rgba(33, 192, 142, 0.55);
  transition:
    transform 160ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 160ms ease,
    filter 160ms ease;
}
.get-started:hover {
  filter: brightness(1.05);
  transform: translateY(-1px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12),
    0 3px 8px rgba(16, 24, 40, 0.2),
    0 14px 26px -10px rgba(33, 192, 142, 0.62);
}
.get-started:active {
  transform: translateY(0);
  filter: brightness(0.99);
}
.get-started > * {
  position: relative;
}

@media (prefers-reduced-motion: reduce) {
  .feature-pill {
    animation: none;
  }
  .get-started {
    transition: none;
  }
}

/* Choreographed exit — classes added by Login's <Transition name="onboard-swap" mode="out-in">.
   The glass panel slides + fades away fast; the textured backdrop (root) fades out
   more slowly; the login page then fades in afterwards (mode="out-in"). */
/* Root carries the (longest) duration so Vue waits for the full exit before the
   login enters. Root opacity is left unchanged — the backdrop fade is driven
   directionally by its own mask below, not a uniform opacity fade. */
.onboard-root.onboard-swap-leave-active {
  transition: opacity 0.6s ease;
}

/* Glass panel: slides + fades away fast */
.onboard-root.onboard-swap-leave-active .hero-card {
  transition:
    opacity 0.33s ease,
    transform 0.4s cubic-bezier(0.4, 0, 0.6, 1);
}
.onboard-root.onboard-swap-leave-to .hero-card {
  opacity: 0;
  transform: translateY(30px) scale(0.97);
}

/* Textured backdrop: fades away from top to bottom, slowly.
   A gradient mask (transparent band at top, opaque at bottom) is swept upward,
   so the transparent region grows from the top down. */
.onboard-root.onboard-swap-leave-active .hero-backdrop {
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, transparent 33%, #000 66%, #000 100%);
  mask-image: linear-gradient(to bottom, transparent 0%, transparent 33%, #000 66%, #000 100%);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 300%;
  mask-size: 100% 300%;
  -webkit-mask-position: 50% 100%;
  mask-position: 50% 100%;
  transition:
    -webkit-mask-position 0.6s ease,
    mask-position 0.6s ease;
}
.onboard-root.onboard-swap-leave-to .hero-backdrop {
  -webkit-mask-position: 50% 0%;
  mask-position: 50% 0%;
}

@media (prefers-reduced-motion: reduce) {
  .onboard-root.onboard-swap-leave-active {
    transition: opacity 0.25s ease;
  }
  .onboard-root.onboard-swap-leave-to {
    opacity: 0;
  }
  .onboard-root.onboard-swap-leave-active .hero-card {
    transition: opacity 0.25s ease;
  }
  .onboard-root.onboard-swap-leave-to .hero-card {
    transform: none;
  }
  .onboard-root.onboard-swap-leave-active .hero-backdrop {
    -webkit-mask-image: none;
    mask-image: none;
  }
}
</style>
